/**
 * Page-structure maths for question group items, shared by the client builder and
 * the partner Library builder. Items live on numbered pages; a "page break" is not
 * a row of its own, it is a renumbering of every item after the break.
 */

export interface PageableItem {
  id: string;
  sortOrder: number;
  pageNumber?: number | null;
}

export interface PageUpdate {
  id: string;
  pageNumber: number;
}

/** Items in the order a respondent meets them: by page, then by position on the page. */
export function sortItemsByPage<T extends PageableItem>(items: T[]): T[] {
  return [...items].sort((a, b) =>
    (a.pageNumber ?? 1) !== (b.pageNumber ?? 1)
      ? (a.pageNumber ?? 1) - (b.pageNumber ?? 1)
      : a.sortOrder - b.sortOrder,
  );
}

export function pageNumbersOf<T extends PageableItem>(sortedItems: T[]): number[] {
  const pages = [...new Set(sortedItems.map(i => i.pageNumber ?? 1))].sort((a, b) => a - b);
  return pages.length === 0 ? [1] : pages;
}

export function groupItemsByPage<T extends PageableItem>(sortedItems: T[]): Map<number, T[]> {
  const byPage = new Map<number, T[]>();
  for (const item of sortedItems) {
    const p = item.pageNumber ?? 1;
    if (!byPage.has(p)) byPage.set(p, []);
    byPage.get(p)!.push(item);
  }
  return byPage;
}

/** A break after `afterItem` pushes everything later than it onto the next page. */
export function addPageBreakUpdates<T extends PageableItem>(sortedItems: T[], afterItem: T): PageUpdate[] {
  const afterPage = afterItem.pageNumber ?? 1;
  return sortedItems
    .filter(i => (i.pageNumber ?? 1) > afterPage || ((i.pageNumber ?? 1) === afterPage && i.sortOrder > afterItem.sortOrder))
    .map(i => ({ id: i.id, pageNumber: (i.pageNumber ?? 1) + 1 }));
}

/** Removing the break before `pageNum` merges that page back into the one before it. */
export function removePageBreakUpdates<T extends PageableItem>(sortedItems: T[], pageNum: number): PageUpdate[] {
  return sortedItems
    .filter(i => (i.pageNumber ?? 1) >= pageNum)
    .map(i => ({ id: i.id, pageNumber: Math.max(1, (i.pageNumber ?? 1) - 1) }));
}

export interface SortOrderUpdate {
  id: string;
  sortOrder: number;
}

/**
 * Moving a row up or down swaps it with its neighbour on the same page, or carries it
 * across the page boundary when it sits at the page's edge (up joins the end of the
 * previous page; down becomes the first row of the next). Returns the changes to
 * persist: pageNumber changes for the bulk update-pages endpoint, and sortOrder
 * rewrites (position on the page) for the pages the move touched. A page emptied by
 * the move closes up, renumbering the pages after it.
 */
export function moveItemUpdates<T extends PageableItem>(
  sortedItems: T[],
  itemId: string,
  direction: 'up' | 'down',
): { pageUpdates: PageUpdate[]; sortUpdates: SortOrderUpdate[] } {
  const none = { pageUpdates: [], sortUpdates: [] };
  const flat = [...sortedItems];
  const idx = flat.findIndex(i => i.id === itemId);
  if (idx === -1) return none;
  const item = flat[idx];
  const itemPage = item.pageNumber ?? 1;

  // Desired end state: the flat order after the move, plus each item's page.
  const newPage = new Map(flat.map(i => [i.id, i.pageNumber ?? 1]));
  if (direction === 'up') {
    if (idx === 0) return none;
    const prev = flat[idx - 1];
    if ((prev.pageNumber ?? 1) === itemPage) {
      [flat[idx - 1], flat[idx]] = [flat[idx], flat[idx - 1]];
    } else {
      newPage.set(item.id, prev.pageNumber ?? 1);
    }
  } else {
    if (idx === flat.length - 1) return none;
    const next = flat[idx + 1];
    if ((next.pageNumber ?? 1) === itemPage) {
      [flat[idx], flat[idx + 1]] = [flat[idx + 1], flat[idx]];
    } else {
      newPage.set(item.id, next.pageNumber ?? 1);
    }
  }

  // Close any gap left by an emptied page: surviving pages renumber to 1..n.
  const distinctPages = [...new Set(flat.map(i => newPage.get(i.id)!))].sort((a, b) => a - b);
  const remap = new Map(distinctPages.map((p, i) => [p, i + 1]));

  const pageUpdates: PageUpdate[] = [];
  for (const i of flat) {
    const target = remap.get(newPage.get(i.id)!)!;
    if (target !== (i.pageNumber ?? 1)) pageUpdates.push({ id: i.id, pageNumber: target });
  }

  // Rewrite sortOrder as position-on-page, but only for the pages the move touched:
  // untouched pages keep their existing (already correctly ordered) values.
  const touched = new Set<number>();
  touched.add(remap.get(newPage.get(item.id)!)!);
  const sourcePage = remap.get(itemPage);
  if (sourcePage !== undefined) touched.add(sourcePage);

  const sortUpdates: SortOrderUpdate[] = [];
  const positionOnPage = new Map<number, number>();
  for (const i of flat) {
    const target = remap.get(newPage.get(i.id)!)!;
    const pos = positionOnPage.get(target) ?? 0;
    positionOnPage.set(target, pos + 1);
    if (touched.has(target) && i.sortOrder !== pos) sortUpdates.push({ id: i.id, sortOrder: pos });
  }

  return { pageUpdates, sortUpdates };
}

/** Questions the respondent has already met when they reach `item`; display conditions may only test these. */
export function precedingItemsFor<T extends PageableItem>(sortedItems: T[], item: T): T[] {
  return sortedItems.filter(i =>
    (i.pageNumber ?? 1) < (item.pageNumber ?? 1) ||
    ((i.pageNumber ?? 1) === (item.pageNumber ?? 1) && i.sortOrder < item.sortOrder),
  );
}
